import GameIds from 'a/hub/data/GameIds';
import { getAuthDataIsPlacementAPT, getAuthDataIsPlacementARBA } from 'a/selectors/persistStateSelectors';
import { getGameState, saveGameState } from 'a/actions/persistStateActions';
import { dispatchEventObj } from 'a/actions/searchlightActionCreators';
import { aquaAxiosWithCreds } from 'root/lib/utilityFunctions';
import { GRADES } from 'a/hub/data/constants';

import {
  RESULTS_API_URL,
  PLACEMENT_TOOL,
  SAVED_DATA_VERSION,
  TRO_BENCHMARK,
  TRO_RESULTS_URL,
  PLACEMENT_TOOL_ES,
  ACTIONS,
  BENCHMARK_MODE_PARAM,
} from '../data/constants';
import { calculatePlacement } from './scoring';
import {
  setMeasuresForGrade,
  setTroRequest,
  setIsValidTroResult,
  resetBenchmarkModeRedirectFlag,
  resetBenchmarkEOY,
} from '../redux/actions';
import {
  getStudentTOY,
  getRemainingMeasures,
  getMeasuresCompleted,
  getBenchmarkEOYPath,
  getTeacherResetInfo,
} from '../redux/selectors';
import * as utility from '../lib/utilityFunctions';
import { PRODUCT_LECTURA } from '../../../lib/constants';

export const loadPlacementProgress = (gameId, data, dispatch) => {
  dispatch(setMeasuresForGrade(data));
  dispatch(getGameState(gameId));
};

export const savePlacementProgress = (data, dispatch, product) => {
  const versionedData = { ...data, version: SAVED_DATA_VERSION };
  try {
    utility.validatePlacementToolData(versionedData);
    const gameStateBucket = product === PRODUCT_LECTURA ? PLACEMENT_TOOL_ES : PLACEMENT_TOOL;
    dispatch(saveGameState(gameStateBucket, versionedData));
  } catch (e) {
    console.error(`There is a problem with the intermediate save object of placement tool: ${e.message}`);
  }
};

export const postPlacementResults = async ({
  grade, toy, results, placements, defaultPlacement, troData = {}, hasTroError = false, product, isDevSkip, totalScore,
}, dispatch) => {
  const placement = isDevSkip ? totalScore : calculatePlacement(results, placements, troData, toy, grade, product);

  if (placement === null || placement === undefined) {
    // Send an error message
    dispatch(dispatchEventObj({
      application: {
        eventType: 'ERROR',
        details: {
          category: 'javascript',
          error: 'Placement/total_score calculated as null or undefined; sending default placement instead',
          data: { grade, toy, results, placements, defaultPlacement, product },
        },
      },
      log: {
        level: 'error',
        logType: 'communication',
        message: 'Placement/total_score calculated as null or undefined; sending default placement instead',
      },
      metric: {
        type: 'count',
        id: 'amplify.AQUA.App.error.javascript',
        value: 1,
      },
    }));

    throw new Error('Placement/total_score calculated as null or undefined; sending default placement instead');
  }

  try {
    const response = await aquaAxiosWithCreds(RESULTS_API_URL, {
      method: 'POST',
      timeout: 20000,
      data: {
        measures: results.map(
          ({
            measure,
            submeasure,
            curriculum,
            total_score,
            possible_score,
            questions,
          }) => ({
            measure,
            submeasure,
            form_id: curriculum,
            total_score,
            possible_score,
            questions,
            Status: 'completed',
          }),
        ),
        toy,
        total_score: placement || defaultPlacement,
        hasTroError,
        // PK and 6-8th graders might get the IPT, but the API can only handle grades 0-5
        assessment_grade: [GRADES.PRE_KINDERGARTEN, GRADES.KINDERGARTEN].includes(grade)
          ? GRADES.KINDERGARTEN : `0${Math.min(Math.max(grade, 0), 5)}`,
        scoring_version: '1.0',
        ...troData.is_complete ? { is_tro_apt: true } : {},
        troResultId: troData.result_id || null,
        product,
      },
    });

    // If you need to do something with the return on this call,
    // here's the data that comes back:
    // const { Placement, placedMethod } = await response.json();
    const responseJson = response.data;

    if (!responseJson || responseJson.Placement === null || responseJson.Placement === undefined) {
      throw new Error('Placement API call failed');
    }

    return placement;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

const attemptTroGetRequest = async (currentAssessment, period, hasTroError, dispatch) => {
  const response = await aquaAxiosWithCreds(`${TRO_RESULTS_URL}?period=${period}`);
  const hasResponse = response && response.data;

  // edge case: TRO voice recording error with logout before returning to AR and hitting play
  if (hasResponse && !response.data?.is_complete && response.data?.is_valid_vr_status === false) {
    dispatch(setIsValidTroResult(currentAssessment, false));
  } else {
    dispatch(setTroRequest(currentAssessment, {
      hasTroError,
      isTroRequestResolved: true,
      isTroRequestFailure: false,
      troData: hasResponse ? response.data : {},
    }));
  }
};

export const getTroResults = async (currentAssessment, period, hasTroError, dispatch) => {
  try {
    await attemptTroGetRequest(currentAssessment, period, hasTroError, dispatch);
  } catch (firstError) {
    // Give TRO GET request 2 tries before giving student default placement if no error from TRO
    // getTroResults is only called with hasTroError true when in Benchmark Mode.
    //   Only make one GET request attempt if known error from TRO in this case.
    const retryAttempts = hasTroError ? 1 : 2;
    console.error(`TRO GET Request Error (Attempt 1 of ${retryAttempts}):`, firstError);
    if (hasTroError) {
      dispatch(setTroRequest(currentAssessment, {
        hasTroError,
        isTroRequestResolved: true,
        isTroRequestFailure: false,
        troData: {},
      }));
      throw firstError;
    } else {
      try {
        await attemptTroGetRequest(currentAssessment, period, hasTroError, dispatch);
      } catch (secondError) {
        // Set TRO request as complete in redux to bypass redirect to TRO url
        dispatch(setTroRequest(currentAssessment, {
          isTroRequestResolved: true,
          isTroRequestFailure: true,
          troData: {},
        }));
        console.error(`TRO GET Request Error (Attempt 2 of ${retryAttempts}):`, secondError);
      }
    }
  }
};

export const sendTroBenchmarkPost = async (toy, teacherResetInfo, isClientReset, hasTroError, dispatch) => {
  if (isClientReset) {
    dispatch(resetBenchmarkModeRedirectFlag(toy));
  }
  const { teacherReset } = teacherResetInfo[toy];
  const troGameData = {
    gameDetails: {
      teacherResetInfo: {
        ...teacherResetInfo,
        [toy]: {
          ...teacherResetInfo[toy],
          hasTroError,
          teacherReset: isClientReset ? false : teacherReset,
        },
      },
    },
  };
  try {
    await dispatch(saveGameState(TRO_BENCHMARK, troGameData));
  } catch (e) {
    console.error(`There is a problem with the intermediate save object of tro benchmark: ${e.message}`);
  }
};

export const sendBenchmarkEOYPost = async (data, dispatch, isReset = false) => {
  if (isReset) {
    dispatch(resetBenchmarkEOY());
  }

  const { benchmarkId, rwrId, benchmarkEOY, hasCompletedRWR, teacherResetInfo } = data;
  const troGameData = {
    benchmarkId: benchmarkId ?? null,
    rwrId: rwrId ?? null,
    benchmarkEOY: isReset ? null : benchmarkEOY,
    hasStartedBenchmarkEOY: !isReset,
    hasCompletedRWR: !!hasCompletedRWR,
    gameDetails: { teacherResetInfo },
  };
  try {
    await dispatch(saveGameState(TRO_BENCHMARK, troGameData));
  } catch (e) {
    console.error(`There is a problem with the intermediate save object of tro benchmark: ${e.message}`);
  }
};

export const setBenchmarkModeRedirectFlag = () => async (dispatch, getState) => {
  const state = getState();
  const isPlacementAPT = getAuthDataIsPlacementAPT(state);
  const benchmarkEOYPath = getBenchmarkEOYPath(state);

  // Edge case where teacherReset flag is true (from BRBA assign or teacher reset from TRO error)
  // and student is to take APT or BenchmarkEOY:
  // Do not set isBenchmarkModeOn flag to true until APT or BenchmarkEOY gets completed.
  if (isPlacementAPT || benchmarkEOYPath) {
    return;
  }

  const toy = getStudentTOY(state);
  const teacherResetInfo = getTeacherResetInfo(state);
  const teacherReset = teacherResetInfo?.[toy]?.teacherReset;
  // If returning from TRO Benchmark, gamestate call will be lost so update redux until outro animation plays.
  const isBenchmarkModeInUrl = new URLSearchParams(window.location.search).get(BENCHMARK_MODE_PARAM) === 'true';
  const shouldShowBenchmarkMode = teacherReset || isBenchmarkModeInUrl;

  // If student did not start or finish ARBA (TRO-APT) and logs out/exits browser
  // (including logging out of AR before starting ARBA as well as not finishing MTG after redirect from TRO error),
  // teacher can administer benchmark from TD.
  // This will enable isBenchmarkModeOn from gamestate, but we want to keep ARBA flow (benchmark url parm = false).
  const isPlacementARBA = getAuthDataIsPlacementARBA(state);
  const hasStartedARBA = getMeasuresCompleted(state).length;
  const hasCompletedARBA = !getRemainingMeasures(state).length;

  if (teacherReset && isPlacementARBA && (!hasStartedARBA || !hasCompletedARBA)) {
    // Keep original ARBA flow (set isBenchmarkModeOn and hasTroError to false) to send student to TRO
    await sendTroBenchmarkPost(toy, teacherResetInfo, shouldShowBenchmarkMode, false, dispatch);
  } else if (shouldShowBenchmarkMode) {
    await sendTroBenchmarkPost(toy, teacherResetInfo, !shouldShowBenchmarkMode, false, dispatch);
    await dispatch({
      gameId: GameIds.TRO_BENCHMARK,
      type: ACTIONS.SET_BENCHMARK_MODE_REDIRECT_FLAG,
      payload: shouldShowBenchmarkMode,
    });
  }
};

export default {
  loadPlacementProgress,
  savePlacementProgress,
  postPlacementResults,
  getTroResults,
  sendTroBenchmarkPost,
  sendBenchmarkEOYPost,
};
